(() => {
  'use strict';

  const onReady = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };

  function initMobileMenu(scope = document) {
    const header = scope.querySelector('[data-site-header]');
    if (!header || header.dataset.menuReady === 'true') return;
    header.dataset.menuReady = 'true';
    const toggle = header.querySelector('[data-mobile-menu-toggle]');
    const drawer = header.querySelector('[data-mobile-drawer]');
    const closers = header.querySelectorAll('[data-mobile-menu-close]');
    if (!toggle || !drawer) return;
    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };
    toggle.addEventListener('click', open);
    closers.forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  }

  function initSlideshows(scope = document) {
    scope.querySelectorAll('[data-slideshow]').forEach((slider) => {
      if (slider.dataset.ready === 'true') return;
      slider.dataset.ready = 'true';
      const slides = Array.from(slider.querySelectorAll('[data-slide]'));
      const dots = Array.from(slider.querySelectorAll('[data-slide-dot]'));
      if (slides.length < 2) return;
      let index = 0;
      const interval = Math.max(Number(slider.dataset.interval) || 7000, 3000);
      let timer;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };
      const start = () => {
        window.clearInterval(timer);
        timer = window.setInterval(() => show(index + 1), interval);
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
      slider.addEventListener('mouseenter', () => window.clearInterval(timer));
      slider.addEventListener('mouseleave', start);
      start();
    });
  }

  function initCollectionTabs(scope = document) {
    scope.querySelectorAll('[data-collection-tabs]').forEach((section) => {
      if (section.dataset.ready === 'true') return;
      section.dataset.ready = 'true';
      const buttons = section.querySelectorAll('[data-collection-tab]');
      const panels = section.querySelectorAll('[data-collection-panel]');
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const key = button.dataset.collectionTab;
          buttons.forEach((b) => {
            const active = b === button;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          panels.forEach((panel) => {
            const active = panel.dataset.collectionPanel === key;
            panel.hidden = !active;
            panel.classList.toggle('is-active', active);
          });
        });
      });
    });
  }

  function initCountdowns(scope = document) {
    scope.querySelectorAll('[data-countdown-section]').forEach((section) => {
      if (section.dataset.ready === 'true') return;
      section.dataset.ready = 'true';
      const hoursEl = section.querySelector('[data-hours]');
      const minutesEl = section.querySelector('[data-minutes]');
      const secondsEl = section.querySelector('[data-seconds]');
      const duration = Math.max(1, Number(section.dataset.hours) || 6) * 60 * 60 * 1000;
      const storageKey = section.dataset.storageKey || 'ainor-sale-countdown';
      let endAt = Date.now() + duration;
      try {
        const saved = Number(localStorage.getItem(storageKey));
        if (saved && saved > Date.now()) endAt = saved;
        else localStorage.setItem(storageKey, String(endAt));
      } catch (_) {}
      const pad = (value) => String(value).padStart(2, '0');
      let timer;
      const tick = () => {
        const remaining = Math.max(0, endAt - Date.now());
        const totalSeconds = Math.floor(remaining / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (hoursEl) hoursEl.textContent = pad(hours);
        if (minutesEl) minutesEl.textContent = pad(minutes);
        if (secondsEl) secondsEl.textContent = pad(seconds);
        if (remaining <= 0) window.clearInterval(timer);
      };
      tick();
      timer = window.setInterval(tick, 1000);
    });
  }

  function initTestimonials(scope = document) {
    scope.querySelectorAll('[data-testimonial-slider]').forEach((slider) => {
      if (slider.dataset.ready === 'true') return;
      slider.dataset.ready = 'true';
      const track = slider.querySelector('[data-testimonial-track]');
      const prev = slider.querySelector('[data-testimonial-prev]');
      const next = slider.querySelector('[data-testimonial-next]');
      if (!track) return;
      const step = () => Math.max(track.clientWidth * (window.innerWidth < 750 ? 1 : 0.7), 260);
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    });
  }

  function initProduct(scope = document) {
    scope.querySelectorAll('[data-product-section]').forEach((section) => {
      if (section.dataset.ready === 'true') return;
      section.dataset.ready = 'true';

      const mainImage = section.querySelector('[data-product-main-image]');
      const thumbs = section.querySelectorAll('[data-product-thumb]');
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!mainImage) return;
          mainImage.src = thumb.dataset.src;
          if (thumb.dataset.srcset) mainImage.srcset = thumb.dataset.srcset;
          mainImage.alt = thumb.dataset.alt || '';
          thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
        });
      });

      const quantity = section.querySelector('[data-quantity-selector]');
      if (quantity) {
        const input = quantity.querySelector('input');
        const minus = quantity.querySelector('[data-quantity-minus]');
        const plus = quantity.querySelector('[data-quantity-plus]');
        minus?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) - 1); });
        plus?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) + 1); });
      }

      const variantSelect = section.querySelector('[data-variant-select]');
      const price = section.querySelector('[data-product-price]');
      if (variantSelect && price) {
        variantSelect.addEventListener('change', () => {
          const option = variantSelect.options[variantSelect.selectedIndex];
          if (option?.dataset.price) price.textContent = option.dataset.price;
        });
      }
    });

    scope.querySelectorAll('[data-product-tabs]').forEach((tabs) => {
      if (tabs.dataset.ready === 'true') return;
      tabs.dataset.ready = 'true';
      const buttons = tabs.querySelectorAll('[data-product-tab-button]');
      const panels = tabs.querySelectorAll('[data-product-tab-panel]');
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const key = button.dataset.productTabButton;
          buttons.forEach((b) => {
            const active = b === button;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          panels.forEach((panel) => {
            const active = panel.dataset.productTabPanel === key;
            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
          });
        });
      });
    });
  }

  function initNewsletterPopup(scope = document) {
    const popup = scope.querySelector('[data-newsletter-popup]');
    if (!popup || popup.dataset.ready === 'true') return;
    popup.dataset.ready = 'true';
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem('ainorNewsletterPopupShown') === '1'; } catch (_) {}
    if (alreadyShown) return;
    const closeButtons = popup.querySelectorAll('[data-popup-close]');
    const close = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };
    closeButtons.forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && popup.classList.contains('is-open')) close(); });
    window.setTimeout(() => {
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      try { sessionStorage.setItem('ainorNewsletterPopupShown', '1'); } catch (_) {}
    }, Number(popup.dataset.delay) || 5000);
  }


  function initSortControls(scope = document) {
    scope.querySelectorAll('[data-sort-by]').forEach((select) => {
      if (select.dataset.ready === 'true') return;
      select.dataset.ready = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });
  }

  function syncFooterAccordions() {
    document.querySelectorAll('.ae-footer__column').forEach((details) => {
      if (window.innerWidth < 750) details.removeAttribute('open');
      else details.setAttribute('open', '');
    });
  }

  function initCollectionFilters(scope = document) {
    scope.querySelectorAll('[data-filter-toggle]').forEach((toggle) => {
      if (toggle.dataset.ready === 'true') return;
      toggle.dataset.ready = 'true';
      const section = toggle.closest('.ae-collection-page');
      const panel = section?.querySelector('[data-filter-panel]');
      const close = section?.querySelector('[data-filter-close]');
      if (!panel) return;
      const openPanel = () => { panel.classList.add('is-open'); document.body.style.overflow = 'hidden'; };
      const closePanel = () => { panel.classList.remove('is-open'); document.body.style.overflow = ''; };
      toggle.addEventListener('click', openPanel);
      close?.addEventListener('click', closePanel);
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closePanel(); });
    });
  }

  function initAll(scope = document) {
    initMobileMenu(scope);
    initSlideshows(scope);
    initCollectionTabs(scope);
    initCountdowns(scope);
    initTestimonials(scope);
    initProduct(scope);
    initNewsletterPopup(scope);
    initSortControls(scope);
    initCollectionFilters(scope);
  }

  onReady(() => {
    initAll(document);
    syncFooterAccordions();
    let resizeTimer;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(syncFooterAccordions, 120);
    });
  });

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
